import { memo, useCallback, type CSSProperties, type MouseEvent } from 'react'
import { useDraggable } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import type { ScheduleItem } from '../../types/index.js'
import {
  ITEM_STATUS_COLORS, ITEM_TYPE_COLORS, formatDurationShort, formatTime, scheduleItemTitle,
} from '../../lib/utils.js'
import type { LayoutItem } from './useAgendaLayout.js'
import { GUTTER_LANES } from './useAgendaLayout.js'
import { selectModeFromEvent, type GridSelectMode } from './useGridSelection.js'

/**
 * La gouttiere des liaisons : la bande etroite ou passent les elements trop
 * courts pour porter leur texte.
 *
 * Ils y sont a leur position exacte, sans hauteur gonflee. Avant, un jingle de
 * 20 s etait dessine sur 14 px dans la colonne des programmes — neuf fois sa
 * duree reelle — et comme il se pose par definition sur une *frontiere*, et
 * qu'un titre de bloc s'ecrit en *haut* de son bloc, il tombait toujours pile
 * sur un titre. Ce n'etait pas un cas limite, c'etait la regle.
 *
 * Le tiret ne porte donc plus de nom, seulement une couleur de type et une
 * position juste ; le nom se lit au survol et dans le panneau de detail. Il
 * reste saisissable a la souris : c'est par lui qu'on deplace un jingle.
 */
export const AgendaMarkerGutter = memo(function AgendaMarkerGutter({
  markers,
  totalHeight,
  selectedItemId,
  isMultiSelected,
  groupDragIds = null,
  onSelect,
  onContextMenu,
  dragLocked = false,
}: {
  markers:         LayoutItem[]
  totalHeight:     number
  selectedItemId:  string | null
  isMultiSelected: (id: string) => boolean
  /** Taquets emportés par le glisser d'un autre membre de la sélection. */
  groupDragIds?:   ReadonlySet<string> | null
  onSelect:        (item: ScheduleItem, mode: GridSelectMode) => void
  onContextMenu?:  (item: ScheduleItem, x: number, y: number) => void
  dragLocked?:     boolean
}) {
  return (
    <div
      className="agenda-gutter"
      style={{ '--timeline-height': `${totalHeight}px` } as CSSProperties}
      aria-label="Liaisons d’antenne"
      role="group"
    >
      {markers.map(marker => (
        <MarkerTick
          key={marker.item.id}
          layoutItem={marker}
          isSelected={marker.item.id === selectedItemId}
          isMultiSelected={isMultiSelected(marker.item.id)}
          isGroupDragging={groupDragIds?.has(marker.item.id) ?? false}
          onSelect={onSelect}
          onContextMenu={onContextMenu}
          dragLocked={dragLocked}
        />
      ))}
    </div>
  )
})

const MarkerTick = memo(function MarkerTick({
  layoutItem,
  isSelected,
  isMultiSelected,
  isGroupDragging,
  onSelect,
  onContextMenu,
  dragLocked,
}: {
  layoutItem:      LayoutItem
  isSelected:      boolean
  isMultiSelected: boolean
  isGroupDragging: boolean
  onSelect:        (item: ScheduleItem, mode: GridSelectMode) => void
  onContextMenu?:  (item: ScheduleItem, x: number, y: number) => void
  dragLocked:      boolean
}) {
  const { item, top, height, lane } = layoutItem
  const isDraggable = !['ON_AIR', 'DONE', 'LOADING'].includes(item.status) && !dragLocked
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: item.id,
    disabled: !isDraggable,
    data: { item },
  })

  // Le taquet n'a pas de texte : sa couleur est tout ce qu'il dit. On y met
  // donc le type — jingle jaune, infoservice brun, pub rose — pour qu'on lise
  // la nature de la liaison d'un coup d'oeil.
  //
  // Deux etats gardent la main sur le type. Ce qui est passe retombe au gris
  // neutre : ce n'est plus a manipuler, ca ne doit plus attirer l'oeil. Ce qui
  // est a l'antenne reste vert, comme son bloc dans la colonne : c'est la seule
  // couleur que la regie lit sans reflechir, elle ne peut pas vouloir dire
  // « jingle » ici et « antenne » deux pixels plus loin. L'erreur, elle, est
  // forcee en rouge par la CSS.
  const statusRules = item.status === 'DONE' || item.status === 'SKIPPED' || item.status === 'ON_AIR'
  const colors = statusRules ? ITEM_STATUS_COLORS[item.status] : ITEM_TYPE_COLORS[item.itemType]

  const style = {
    '--agenda-tick-top':    `${top}px`,
    '--agenda-tick-height': `${height}px`,
    // Le couloir est une fraction de la gouttiere : la CSS n'a pas a connaitre
    // sa largeur en pixels, elle la tient de sa propre boite.
    '--agenda-tick-left':   `${(lane / GUTTER_LANES) * 100}%`,
    '--agenda-tick-width':  `${100 / GUTTER_LANES}%`,
    '--agenda-tick-color':  colors.border,
    '--agenda-tick-transform': CSS.Translate.toString(transform) || 'none',
  } as CSSProperties

  const handleClick = useCallback((event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation()
    if (event.shiftKey) window.getSelection()?.removeAllRanges()
    onSelect(item, selectModeFromEvent(event))
  }, [item, onSelect])
  const handleContextMenu = useCallback((event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    onContextMenu?.(item, event.clientX, event.clientY)
  }, [item, onContextMenu])

  const overrideTitle = item.overrides && typeof item.overrides.title === 'string' ? item.overrides.title : null
  const title = scheduleItemTitle(item, null) ?? overrideTitle ?? 'Élément'

  const classes = [
    'agenda-tick',
    `is-type-${item.itemType.toLowerCase().replace('_', '-')}`,
    isDraggable ? 'is-draggable' : '',
    isDragging ? 'is-dragging' : '',
    isGroupDragging ? 'is-group-dragging' : '',
    isSelected ? 'is-selected' : '',
    isMultiSelected ? 'is-multi-selected' : '',
    item.status === 'ON_AIR' ? 'is-on-air' : '',
    item.status === 'ERROR' ? 'is-error' : '',
  ].filter(Boolean).join(' ')

  return (
    <div
      ref={setNodeRef}
      className={classes}
      style={style}
      title={`${formatTime(item.startTime)} — ${title} · ${formatDurationShort(item.durationSec)}`}
      {...attributes}
      {...listeners}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
    >
      {/* Le taquet est trop fin pour se lire : pendant qu'il glisse, son titre
          l'accompagne, au lieu d'une vignette flottante détachée de lui. */}
      {isDragging && <span className="agenda-tick__drag-label">{title}</span>}
    </div>
  )
})

export default AgendaMarkerGutter
